import apiClient from './apiClient';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Visit Metadata API — Notes, Vitals, Symptoms for visits
 *
 * Endpoints:
 *   POST /api/organisation/visits/:id/notes      — Add note
 *   POST /api/organisation/visits/:id/vitals     — Add vitals
 *   POST /api/organisation/visits/:id/symptoms   — Add symptoms
 *   GET  /api/organisation/visits/:id/metadata    — Get all metadata
 *   DELETE /api/organisation/visits/:id/metadata/:type/:metaId — Delete
 *   GET  /api/organisation/config/vitals          — Vital definitions
 *   GET  /api/organisation/config/symptoms        — Symptom suggestions
 * ═══════════════════════════════════════════════════════════════════════
 */

// ── Add Note ─────────────────────────────────────────────────────────

export const addNote = async (
  visitId,
  content,
  { toothNumbers = null, kind = null, severity = null } = {},
) => {
  try {
    const body = { content };
    // Phase 3.1.1 — tooth-tagged notes piggy-back on the existing
    // visit-NOTE meta. Tokens are FDI / Q1–Q4 / FM strings; null on
    // non-dental notes (legacy callers don't pass the option).
    if (toothNumbers && toothNumbers.length > 0) {
      body.tooth_numbers = toothNumbers;
    }
    // `kind` discriminates "note" (default) vs "diagnosis"; `severity`
    // is only meaningful when kind=diagnosis. Pass through only when set
    // so legacy callers stay byte-identical.
    if (kind && kind !== 'note') {
      body.kind = kind;
    }
    if (severity) {
      body.severity = severity;
    }
    const response = await apiClient.post(
      `/api/organisation/visits/${visitId}/notes`,
      body,
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error adding note:', error);
    throw buildApiError(error);
  }
};

// ── Add Procedure Note (Phase 1b) ────────────────────────────────────

export const addProcedureNote = async (visitId, { content, linked_sitting_id }) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/visits/${visitId}/procedure-notes`,
      { content, linked_sitting_id },
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error adding procedure note:', error);
    throw buildApiError(error);
  }
};

// ── Add Vitals ───────────────────────────────────────────────────────

export const addVitals = async (visitId, vitals) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/visits/${visitId}/vitals`,
      { vitals }
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error adding vitals:', error);
    throw buildApiError(error);
  }
};

// ── Add Symptoms ─────────────────────────────────────────────────────

export const addSymptoms = async (visitId, symptoms) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/visits/${visitId}/symptoms`,
      { symptoms }
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error adding symptoms:', error);
    throw buildApiError(error);
  }
};

// ── Get Visit Metadata ───────────────────────────────────────────────

export const fetchVisitMetadata = async (visitId, type = null) => {
  try {
    const params = {};
    if (type) params.type = type;
    const response = await apiClient.get(
      `/api/organisation/visits/${visitId}/metadata`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error fetching metadata:', error);
    throw buildApiError(error);
  }
};

// ── Delete Metadata Entry ────────────────────────────────────────────

export const deleteMetadata = async (visitId, metaType, metaId) => {
  try {
    const response = await apiClient.delete(
      `/api/organisation/visits/${visitId}/metadata/${metaType}/${metaId}`
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error deleting metadata:', error);
    throw buildApiError(error);
  }
};

// ── Upload Prescription ──────────────────────────────────────────────

export const uploadPrescription = async (visitId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `/api/organisation/visits/${visitId}/prescription`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error uploading prescription:', error);
    throw buildApiError(error);
  }
};

// ── Upload Prescription Image Attachments (Custom Theme) ───────────────

export const uploadPrescriptionImages = async (visitId, prescriptionMetaId, files) => {
  try {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    const response = await apiClient.post(
      `/api/organisation/visits/${visitId}/prescription/${prescriptionMetaId}/images`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error uploading prescription images:', error);
    throw buildApiError(error);
  }
};

export const getPrescriptionImages = async (visitId, prescriptionMetaId) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/visits/${visitId}/prescription/${prescriptionMetaId}/images`
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error fetching prescription images:', error);
    throw buildApiError(error);
  }
};

// ── Upload Report ────────────────────────────────────────────────────

export const uploadReport = async (visitId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `/api/organisation/visits/${visitId}/report`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error uploading report:', error);
    throw buildApiError(error);
  }
};

// ── Get File Download URL (legacy — presigned URL) ───────────────────

export const getFileDownloadUrl = async (visitId, metaType, metaId) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/visits/${visitId}/file/${metaType}/${metaId}/url`
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error getting file URL:', error);
    throw buildApiError(error);
  }
};

// ── Secure File Download (streaming proxy — no presigned URL) ────────

export const downloadFileSecure = async (visitId, metaType, metaId) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/visits/${visitId}/file/${metaType}/${metaId}/download`,
      { responseType: 'blob' }
    );
    const blob = response.data;
    const contentType = response.headers['content-type'] || 'application/octet-stream';
    const typedBlob = new Blob([blob], { type: contentType });
    const blobUrl = URL.createObjectURL(typedBlob);
    return { blobUrl, contentType };
  } catch (error) {
    console.error('[MetadataAPI] Error downloading file:', error);
    throw buildApiError(error);
  }
};

// ── Config: Vital Definitions ────────────────────────────────────────

export const fetchVitalDefinitions = async () => {
  try {
    const response = await apiClient.get('/api/organisation/config/vitals');
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error fetching vital defs:', error);
    throw buildApiError(error);
  }
};

// ── Config: Symptom Suggestions ──────────────────────────────────────

export const fetchSymptomSuggestions = async (specialization) => {
  try {
    const response = await apiClient.get('/api/organisation/config/symptoms', {
      params: { specialization },
    });
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error fetching symptom suggestions:', error);
    throw buildApiError(error);
  }
};

// ── Add Digital Prescription (Structured) ────────────────────────────

export const addDigitalPrescription = async (visitId, medicines, doctorNotes = null, clinicalData = {}) => {
  try {
    const body = {
      medicines,
      chief_complaint: clinicalData.chief_complaint || null,
      diagnosis: clinicalData.diagnosis || null,
      allergies: clinicalData.allergies || null,
      doctor_notes: doctorNotes,
      report_required: clinicalData.report_required || false,
      investigations: clinicalData.investigations || [],
      // G2 (FE-6) — flat top-level `advised_surgeries`, the doctor's typed
      // lines only, default []. The body is `extra = "forbid"` server-side, so
      // this stays an explicit allowlist: no derived/read-only field is echoed.
      advised_surgeries: clinicalData.advised_surgeries || [],
      follow_up_required: clinicalData.follow_up_required || false,
      follow_up_in: clinicalData.follow_up_in || null,
    };
    // Include vitals snapshot so they can be restored on edit
    if (clinicalData.vitals && clinicalData.vitals.length > 0) {
      body.vitals = clinicalData.vitals;
    }
    // Post-completion versioning: pass the meta_id being replaced so
    // the backend soft-supersedes it instead of requiring a hard delete
    if (clinicalData.replaces_meta_id) {
      body.replaces_meta_id = clinicalData.replaces_meta_id;
    }
    if (clinicalData.edit_reason) {
      body.edit_reason = clinicalData.edit_reason;
    }
    const response = await apiClient.post(
      `/api/organisation/visits/${visitId}/prescription/digital`,
      body
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error adding digital prescription:', error);
    throw buildApiError(error);
  }
};

// ── Parse Prescription Shorthand ─────────────────────────────────────

export const parsePrescription = async (typedText) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/prescription/parse`,
      { typed_text: typedText },
      { timeout: 3000 } // T-F1: per-call cap on this endpoint only — global client timeout stays 30s
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error parsing prescription:', error);
    throw buildApiError(error);
  }
};

// ── Log Prescription Edit (Learning) ─────────────────────────────────

export const logPrescriptionEdit = async (payload) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/prescription/log-edit`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error logging prescription edit:', error);
    // Non-critical — don't throw; learning is best-effort
    return null;
  }
};

// ── Prescription Suggestions (Autocomplete) ──────────────────────────

// T2b — `limit` is additive on the BROWSE path only (server default 8,
// ge=1 le=500); byte-identical otherwise. Omitted entirely when not passed,
// so an old server that doesn't know the param still gets today's request.
export const fetchPrescriptionSuggestions = async (query, { limit } = {}) => {
  try {
    const params = { q: query };
    if (limit != null) params.limit = limit;
    const response = await apiClient.get(
      `/api/organisation/prescription/suggest`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error fetching prescription suggestions:', error);
    throw buildApiError(error);
  }
};

// ── Prescription Reference Data ──────────────────────────────────────

// `visitId` present ⇒ the server resolves the memory owner from the VISIT'S
// DOCTOR (Q9=A) — used on every pad mount. Absent ⇒ the caller's own memory
// (PracticePrescriptionPage, where there is no visit). Never a `member_id` —
// the API does not accept one (build-plan.md §2 G1).
export const fetchPrescriptionReference = async (visitId) => {
  try {
    const params = visitId ? { visit_id: visitId } : undefined;
    const response = await apiClient.get(`/api/organisation/prescription/reference`, { params });
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error fetching prescription reference:', error);
    throw buildApiError(error);
  }
};

// ── Forget a remembered entry (FE-12, frozen G5) ─────────────────────
// `visitId` present ⇒ the server resolves the memory owner from the VISIT'S
// DOCTOR (Q9=A), the SAME optional `?visit_id=` G1/G2 already use — never a
// `member_id`. Body is the exact allowlist G5 accepts (`extra="forbid"`):
// `type` + `value` only. `404` (already gone) and `200` are both a genuine
// "the row isn't there any more" outcome to the caller — this function does
// not special-case either; the caller (DigitalPrescriptionForm) treats a
// thrown `NOT_FOUND` the same as success.
export const forgetPrescriptionMemory = async (type, value, visitId) => {
  try {
    const params = visitId ? { visit_id: visitId } : undefined;
    const response = await apiClient.delete(
      `/api/organisation/prescription/memory`,
      { data: { type, value }, params }
    );
    return response.data;
  } catch (error) {
    console.error('[MetadataAPI] Error forgetting prescription memory:', error);
    throw buildApiError(error);
  }
};

// ── Error helper ─────────────────────────────────────────────────────

function buildApiError(error) {
  const apiError = new Error(error.message || 'An unexpected error occurred');

  if (error.response?.status === 401) {
    apiError.type = 'UNAUTHORIZED';
    apiError.message = 'Session expired. Please login again.';
  } else if (error.response?.status === 403) {
    apiError.type = 'FORBIDDEN';
    apiError.message = error.response.data?.detail || 'You do not have permission for this action.';
  } else if (error.response?.status === 404) {
    apiError.type = 'NOT_FOUND';
    apiError.message = error.response.data?.detail || 'Resource not found.';
  } else if (error.response?.status === 400) {
    apiError.type = 'BAD_REQUEST';
    apiError.message = error.response.data?.detail || 'Invalid request.';
  } else if (error.response?.status === 422) {
    apiError.type = 'VALIDATION';
    const detail = error.response.data?.detail;
    if (Array.isArray(detail)) {
      apiError.message = detail.map(d => d.msg || JSON.stringify(d)).join('; ');
    } else {
      apiError.message = typeof detail === 'string' ? detail : 'Validation error. Please check your input.';
    }
  } else if (error.code === 'ECONNABORTED' || !error.response) {
    apiError.type = 'NETWORK';
    apiError.message = 'Network error. Please check your connection.';
  } else {
    apiError.type = 'UNKNOWN';
    const rawDetail = error.response?.data?.detail;
    apiError.message = typeof rawDetail === 'string' ? rawDetail : (error.message || 'An unexpected error occurred');
  }

  return apiError;
}

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  addNote,
  addProcedureNote,
  addVitals,
  addSymptoms,
  addDigitalPrescription,
  parsePrescription,
  logPrescriptionEdit,
  fetchPrescriptionSuggestions,
  fetchPrescriptionReference,
  forgetPrescriptionMemory,
  uploadPrescription,
  uploadReport,
  getFileDownloadUrl,
  fetchVisitMetadata,
  deleteMetadata,
  fetchVitalDefinitions,
  fetchSymptomSuggestions,
};
